import React from 'react';
import { ExternalLink, Shield, Award, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import OrganizationSchema from '@/components/schema/OrganizationSchema';

const Accreditations = () => {
  const accreditations = [
    {
      name: 'Elmhurst Energy',
      logo: 'https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/b08gspqy_image.png',
      description: 'Elmhurst Energy is the UK\'s leading accreditation scheme for domestic and non-domestic energy assessors. Our assessors are fully accredited to carry out EPCs and energy advice reports to the highest industry standards.',
      website: 'https://www.elmhurstenergy.co.uk/',
      benefits: [
        'Recognized industry standard',
        'Rigorous assessment standards',
        'Continuous professional development',
        'Quality assurance program'
      ]
    },
    {
      name: 'TrustMark',
      logo: 'https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/ejoqsqlu_image.png',
      description: 'TrustMark is the Government Endorsed Quality Scheme covering work in and around the home. As TrustMark registered businesses, we meet high standards of technical competence and customer service, giving you complete peace of mind.',
      website: 'https://www.trustmark.org.uk/homeowner',
      benefits: [
        'Government-endorsed scheme',
        'Consumer protection',
        'High service standards',
        'Independent dispute resolution'
      ]
    },
    {
      name: 'Quidos',
      logo: 'https://customer-assets.emergentagent.com/job_271fb955-65c1-4026-a81a-14741ac1350f/artifacts/rcgls708_cropped-cropped-cropped-Quidos-logo-RGB-NEG.jpeg',
      description: 'Quidos is a leading accreditation scheme for energy assessors, providing quality assurance and professional standards for the energy assessment industry. Our Quidos accreditation ensures we maintain the highest levels of professionalism and accuracy in all our assessments.',
      website: 'https://www.quidos.co.uk/',
      benefits: [
        'Industry recognition',
        'Professional standards',
        'Quality assurance',
        'Continuous assessment'
      ]
    },
    {
      name: 'Buy With Confidence',
      logo: 'https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/gfybwp7x_image.png',
      description: 'Buy With Confidence is a Trading Standards approved scheme. Members are vetted by Trading Standards and must meet high standards of customer service and business practice. This membership demonstrates our commitment to ethical trading and customer satisfaction.',
      website: 'https://www.buywithconfidence.gov.uk/',
      benefits: [
        'Trading Standards approved',
        'Rigorous vetting process',
        'Consumer confidence',
        'Complaint resolution support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Accreditations | Elmhurst, TrustMark, Quidos | Energy Performance Direct"
        description="Energy Performance Direct is fully accredited by Elmhurst Energy, TrustMark, Quidos, and Buy With Confidence. Government-endorsed and Trading Standards approved energy assessors in Exeter and South West UK."
        keywords="Elmhurst Energy accreditation, TrustMark EPC, Quidos certified assessor, Buy With Confidence Exeter, accredited energy assessor"
        canonical="/accreditations"
      />
      <OrganizationSchema />
      <Breadcrumbs items={[
        { name: 'Accreditations', path: '/accreditations' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="accreditations-heading">
              Our Accreditations
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              Fully accredited to the highest industry standards
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full mb-6">
              <Award className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-lg text-gray-700 mb-4">
              At Energy Performance Direct, we are proud to be accredited by leading industry bodies. Our accreditations demonstrate our commitment to professional excellence, quality assurance, and customer protection.
            </p>
            <p className="text-base text-gray-600">
              When you choose Energy Performance Direct for your energy assessment needs in Exeter, Devon, Cornwall, Somerset, or anywhere across the South West UK, you can trust that our assessments are carried out to the highest professional standards and backed by government-endorsed quality schemes.
            </p>
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="space-y-8 max-w-5xl mx-auto">
            {accreditations.map((accreditation, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`accreditation-card-${index}`}>
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Logo */}
                    <div className="flex flex-col items-center justify-center">
                      {accreditation.logo ? (
                        <img 
                          src={accreditation.logo} 
                          alt={accreditation.name} 
                          className="h-24 mb-4"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full flex items-center justify-center mb-4">
                          <Shield className="w-12 h-12 text-green-600" />
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 text-center">{accreditation.name}</h3>
                      {accreditation.website && (
                        <a 
                          href={accreditation.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`accreditation-link-${index}`}
                        >
                          <Button variant="link" size="sm" className="text-green-600 hover:text-green-700 mt-2">
                            Visit Website
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </a>
                      )}
                    </div>

                    {/* Description & Benefits */}
                    <div className="md:col-span-2">
                      <p className="text-gray-700 mb-4">{accreditation.description}</p>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Benefits:</h4>
                        <ul className="space-y-2">
                          {accreditation.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Accreditations Matter</h2>
            <p className="text-gray-700 mb-4">
              Our accreditations aren't just badges - they represent our ongoing commitment to professional excellence, customer protection, and industry best practices. When you choose Energy Performance Direct, you're choosing a trusted, verified, and accountable service provider.
            </p>
            <p className="text-base text-gray-600 mb-8">
              Every one of our energy assessments across Exeter, Plymouth, Bristol, and the wider South West region is carried out by fully qualified, continuously assessed, and independently verified professionals. Our membership in these schemes means we are regularly audited, and our work is quality-checked to ensure accuracy and compliance with all current regulations.
            </p>
            <Button 
              onClick={() => window.location.href = '/booking'}
              data-testid="accreditations-book-btn"
              size="lg" 
              className="text-white px-12 rounded-full"
              style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
            >
              Book Your Assessment
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Accreditations;